import * as React from 'react';
import { useContext, useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { Globe, PanelLeft, SquareCheck } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import { Flex, FlexColumn } from 'src/components/flex';
import { DesktopNavigationInboxesLink } from 'src/components/layout/landlord/DesktopNavigationInboxesLink';
import { DesktopNavigationPropertiesLink } from 'src/components/layout/landlord/DesktopNavigationPropertiesLink';
import { DesktopNavigationTeamLink } from 'src/components/layout/landlord/DesktopNavigationTeamLink';
import { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import { links } from 'src/components/layout/landlord/links';
import NavigationLink, { FeatureFlagNavLink } from 'src/components/layout/landlord/NavigationLink';
import Link from 'src/components/link/Link';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import Responsive from 'src/components/responsive/Responsive';
import { LANDLORD_HOME_PATH } from 'src/routes';
import { useLoggedInUser } from 'src/services/api';
import { ThemeProvider } from 'src/theme';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { getTimezoneAbbr } from 'src/utils/format-date-time';
import { LandlordTeamSelectDialog } from 'src/components/layout/landlord/LandlordTeamSelect';
import NavigationSidebarMenu from 'src/components/layout/landlord/NavigationSidebarMenu';
import SetAwayConfirmationModal from 'src/components/layout/landlord/SetAwayConfirmationModal';
import ActivityCalendar from 'src/components/layout/landlord/ActivityCalendar';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import { isUserAway, EPresence } from 'src/api/users-api';
import PresenceDot from 'src/components/PresenceDot';

import navbarStyles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';
import layoutStyles from 'src/components/layout/landlord/LandlordLayout.module.css';


type Props = {
    onNavigationChange: () => void;
}

export const SYSTEM_TIMEZONE_PREFIX = 'System timezone';

export default function DesktopNavigationSidebar({
    onNavigationChange
}: Props) {
    const { isSidebarCollapsed, setSidebarCollapsed, sidebarWidth } = useLandlordLayoutContext();
    const { data: loggedInUser } = useLoggedInUser();
    const authenticatedState = useContext(AuthenticatedStateContext);
    const timezoneOverride = authenticatedState?.userSettings?.timezone_override ?? null;
    const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneLabel = timezoneOverride ?
        `${timezoneOverride} (${getTimezoneAbbr(timezoneOverride)})` :
        `${SYSTEM_TIMEZONE_PREFIX} (${systemTimezone})`;
    const activeTimezone = timezoneOverride ?? systemTimezone;
    const sidebarTimezoneLabel = activeTimezone.replace(/_/g, ' ');
    const [isProfileMenuOpened, setIsProfileMenuOpened] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isTeamSwitcherOpen, setIsTeamSwitcherOpen] = useState(false);
    const [isSetAwayModalOpen, setIsSetAwayModalOpen] = useState(false);
    const flags = useFlags([EFeatureFlag.FEATURE_FLAG_ROUTE, EFeatureFlag.TRIAGE_INBOX_PAGE, EFeatureFlag.TEAM_AVAILABILITY]);
    const isAvailabilityEnabled = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;
    const isAway = isUserAway(loggedInUser);

    const handleClickSidebar = () => {
        setSidebarCollapsed(!isSidebarCollapsed);
    };


    const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setIsProfileMenuOpened(true);
    };

    const handleOpenTeamSwitcher = () => {
        setIsTeamSwitcherOpen(true);
        setIsProfileMenuOpened(false);
    };

    return (
        <>
            <Responsive desktop>
                <Box
                    className={clsx(
                        layoutStyles.sidebar,
                        layoutStyles.sidebarPadding,
                    )}
                    sx={{
                        width: sidebarWidth,
                        minWidth: sidebarWidth,
                    }}
                />
            </Responsive>

            <ThemeProvider>
                <Responsive desktop>
                    <Paper
                        square
                        className={layoutStyles.sidebar}
                        sx={{ width: sidebarWidth, minWidth: sidebarWidth }}
                    >
                        <FlexColumn height="100vh" justifyContent="space-between" className={navbarStyles.navbarFlexContainer}>
                            {/* this is the navigation bar header */}
                            <Flex flexDirection={isSidebarCollapsed ? 'column' : 'row'} justifyContent="space-between" pr={0.5}>
                                <Link href={LANDLORD_HOME_PATH} data-testid="home-link">
                                    <Flex height={60} p={1} alignItems="center">
                                        <img
                                            src="/img/reffie-logos/brain-transparent-256.png"
                                            alt="reffie-logo"
                                            height="40px"
                                        />
                                        {!isSidebarCollapsed && (
                                            <Typography color="primary" fontWeight="bold">
                                                Reffie
                                            </Typography>
                                        )}
                                    </Flex>
                                </Link>

                                <IconButton
                                    onClick={handleClickSidebar}
                                    data-testid="collapse-toggle"
                                    sx={{
                                        width: isSidebarCollapsed ? 60 : 'auto',
                                    }}
                                >
                                    <PanelLeft size={22} />
                                </IconButton>
                            </Flex>

                            {/* these are the main nav links */}
                            <FlexColumn className="prettyScrollbar" flex={1} sx={{ overflowY: 'auto' }}>
                                <List>
                                    {flags.feature_flag_route?.enabled && <FeatureFlagNavLink onNavigationChange={onNavigationChange} />}
                                    {flags.triage_inbox_page?.enabled && (
                                        <NavigationLink
                                            text={isSidebarCollapsed ? '' : 'Bulk Triage'}
                                            icon={<SquareCheck />}
                                            link="/landlord/inner/bulk-triage"
                                            onClick={onNavigationChange}
                                        />
                                    )}
                                    <DesktopNavigationInboxesLink onNavigationChange={onNavigationChange} isSidebarCollapsed={isSidebarCollapsed} />
                                    {links.map((link) => {
                                        if (link.text === 'Properties') {
                                            return (
                                                <DesktopNavigationPropertiesLink
                                                    key="properties"
                                                    isSidebarCollapsed={isSidebarCollapsed}
                                                    onNavigationChange={onNavigationChange}
                                                />
                                            );
                                        }
                                        if (link.text === 'Team' && (flags.team_availability?.enabled || FORCE_TEAM_AVAILABILITY)) {
                                            return (
                                                <DesktopNavigationTeamLink
                                                    key="team"
                                                    isSidebarCollapsed={isSidebarCollapsed}
                                                    onNavigationChange={onNavigationChange}
                                                />
                                            );
                                        }
                                        return (
                                            <NavigationLink
                                                key={link.text}
                                                text={isSidebarCollapsed ? '' : link.text}
                                                icon={link.icon}
                                                link={link.link}
                                                onClick={onNavigationChange}
                                            />
                                        );
                                    })}
                                </List>
                            </FlexColumn>

                            <FlexColumn>
                                {!isSidebarCollapsed &&
                                    <Box sx={{ px: 1.5 }}>
                                        <ActivityCalendar />
                                    </Box>
                                }
                                {loggedInUser && (
                                    <div className={navbarStyles.userSectionWrap}>
                                        <button
                                            className={clsx(navbarStyles.userSection, isSidebarCollapsed && navbarStyles.userSectionCollapsed)}
                                            onClick={handleOpenProfileMenu}
                                            data-testid="user-account-menu-toggle"
                                        >
                                            <span className={navbarStyles.avatarWrapper}>
                                                <ProfileImageRounded url={loggedInUser.profile_photo_url} userName={loggedInUser.name} width={36} height={36} />
                                                {isAvailabilityEnabled && (
                                                    <>
                                                        <span className={clsx(navbarStyles.avatarRing, isAway ? navbarStyles.avatarRingAway : navbarStyles.avatarRingOnline)} />
                                                        <span className={navbarStyles.presenceDot}>
                                                            <PresenceDot presence={isAway ? EPresence.AWAY : EPresence.ONLINE} size={9} withBorder />
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                            {!isSidebarCollapsed && (
                                                <>
                                                    <div className={navbarStyles.userInfo}>
                                                        <Tooltip title={loggedInUser.name}>
                                                            <Typography variant="body2" className={clsx(navbarStyles.userName, 'ellipsisText')}>
                                                                {loggedInUser.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        {loggedInUser?.team?.name && (
                                                            <Tooltip title={loggedInUser.team.name}>
                                                                <Typography variant="caption" className={clsx(navbarStyles.userTeam, 'ellipsisText')}>
                                                                    {loggedInUser.team.name}
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title={timezoneLabel}>
                                                            <Typography variant="caption" className={clsx(navbarStyles.userTimezone, navbarStyles.userTimezoneRow, 'ellipsisText')}>
                                                                <Globe size={10} className={navbarStyles.timezoneGlobeIcon} />
                                                                {sidebarTimezoneLabel}
                                                            </Typography>
                                                        </Tooltip>
                                                    </div>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                                {isTeamSwitcherOpen && <LandlordTeamSelectDialog open={isTeamSwitcherOpen} onClose={() => setIsTeamSwitcherOpen(false)} />}
                                {isProfileMenuOpened && (
                                    <NavigationSidebarMenu
                                        anchorEl={anchorEl}
                                        setIsProfileMenuOpened={setIsProfileMenuOpened}
                                        isSidebarCollapsed={isSidebarCollapsed}
                                        loggedInUser={loggedInUser!}
                                        openTeamSwitcher={handleOpenTeamSwitcher}
                                        onRequestSetAway={() => setIsSetAwayModalOpen(true)}
                                    />
                                )}
                                {isSetAwayModalOpen && (
                                    <SetAwayConfirmationModal onClose={() => setIsSetAwayModalOpen(false)} />
                                )}
                            </FlexColumn>
                        </FlexColumn>
                    </Paper>
                </Responsive>
            </ThemeProvider>
        </>
    );
}
